import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, switchMap, map, catchError, tap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { Router } from '@angular/router';
import { PartnersActions } from './partners.actions';
import { PartnerService } from '../../services/partner.service';
import { EncryptionService } from '../../zk/encryption.service';
import { Partner, PartnerWithEncryptedPii } from '../../models/partner.model';
import { decryptCustomerRecordList } from '../../models/customer-decrypt.util';
import { decryptInBatches } from '../../zk/batch-decrypt.util';
import { AuditService } from '../../zk/audit.service';

@Injectable()
export class PartnersEffects {
  private actions$ = inject(Actions);
  private partnerService = inject(PartnerService);
  private encryption = inject(EncryptionService);
  private router = inject(Router);
  private audit = inject(AuditService);

  loadPartners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.loadPartners),
      switchMap(() =>
        this.partnerService.getAll().pipe(
          mergeMap(records => from(this.decryptList(records))),
          map(partners => PartnersActions.loadPartnersSuccess({ partners })),
          catchError(err => of(PartnersActions.loadPartnersFailure({ error: err.message })))
        )
      )
    )
  );

  loadPartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.loadPartner),
      mergeMap(({ id }) =>
        this.partnerService.getById(id).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(partner => PartnersActions.loadPartnerSuccess({ partner })),
          catchError(err => of(PartnersActions.loadPartnerFailure({ error: err.message })))
        )
      )
    )
  );

  createPartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.createPartner),
      mergeMap(({ dto }) =>
        this.partnerService.create(dto).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(partner => PartnersActions.createPartnerSuccess({ partner })),
          catchError(err => of(PartnersActions.createPartnerFailure({ error: err.message })))
        )
      )
    )
  );

  createPartnerEncrypted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.createPartnerEncrypted),
      mergeMap(({ dto }) =>
        this.partnerService.createEncrypted(dto).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(partner => PartnersActions.createPartnerSuccess({ partner })),
          catchError(err => of(PartnersActions.createPartnerFailure({ error: err.message })))
        )
      )
    )
  );

  updatePartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.updatePartner),
      mergeMap(({ id, dto }) =>
        this.partnerService.update(id, dto).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(partner => PartnersActions.updatePartnerSuccess({ partner })),
          catchError(err => of(PartnersActions.updatePartnerFailure({ error: err.message })))
        )
      )
    )
  );

  updatePartnerEncrypted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.updatePartnerEncrypted),
      mergeMap(({ id, dto }) =>
        this.partnerService.updateEncrypted(id, dto).pipe(
          mergeMap(record => from(this.decryptOne(record))),
          map(partner => PartnersActions.updatePartnerSuccess({ partner })),
          catchError(err => of(PartnersActions.updatePartnerFailure({ error: err.message })))
        )
      )
    )
  );

  navigateAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.createPartnerSuccess, PartnersActions.updatePartnerSuccess),
      tap(() => this.router.navigate(['/partners']))
    ),
    { dispatch: false }
  );

  // Phase 8.6 — audit a detail-view decrypt (single-record load only, not the list load).
  auditDecrypt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.loadPartnerSuccess),
      tap(({ partner }) => this.audit.log('DECRYPT', 'Partner', partner.id))
    ),
    { dispatch: false }
  );

  deletePartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.deletePartner),
      mergeMap(({ id }) =>
        this.partnerService.delete(id).pipe(
          map(() => PartnersActions.deletePartnerSuccess({ id })),
          catchError(err => of(PartnersActions.deletePartnerFailure({ error: err.message })))
        )
      )
    )
  );

  loadPartnerCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnersActions.loadPartnerCustomers),
      switchMap(({ partnerId }) =>
        this.partnerService.getCustomers(partnerId).pipe(
          mergeMap(records => from(decryptCustomerRecordList(this.encryption, records))),
          map(customers => PartnersActions.loadPartnerCustomersSuccess({ customers })),
          catchError(err => of(PartnersActions.loadPartnerCustomersFailure({ error: err.message })))
        )
      )
    )
  );

  // ── Decryption helpers ────────────────────────────────────────────────────

  private async decryptList(records: PartnerWithEncryptedPii[]): Promise<Partner[]> {
    return decryptInBatches(records, r => this.decryptOne(r));
  }

  private async decryptOne(record: PartnerWithEncryptedPii): Promise<Partner> {
    if (!record.wrappedDek || Object.keys(record.encryptedFields).length === 0) {
      return this.toPlainPartner(record, {});
    }
    try {
      const plain = await this.encryption.decryptRecord(record.encryptedFields, record.wrappedDek);
      return this.toPlainPartner(record, plain);
    } catch {
      return this.toPlainPartner(record, {});
    }
  }

  private toPlainPartner(record: PartnerWithEncryptedPii, plain: Record<string, string>): Partner {
    return {
      id:            record.id,
      name:          plain['name']          ?? record.name,
      address:       plain['address']       ?? record.address,
      contactPerson: plain['contactPerson'] ?? record.contactPerson,
      phone:         plain['phone']         ?? record.phone,
      email:         plain['email']         ?? record.email,
      notes:         record.notes,
      isActive:      record.isActive,
      excelMappings: record.excelMappings,
    };
  }
}
